import React, { ReactNode } from "react"

import { format } from "date-fns"
import { CalendarIcon, MapPinIcon, UsersIcon } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"

interface EventCardProps {
  date: Date
  location: string
  timeInfo: string
  capacity: number
  registered: number
  actionLabel?: string
  bookingDisabled?: boolean
  title: ReactNode
  attendeeIcon: ReactNode
  attendeeInfo: ReactNode
  badge?: ReactNode
  status?: "Open" | "Closed" | "Deleted"
}

const EventCard: React.FC<EventCardProps> = ({
  date,
  location,
  timeInfo,
  capacity,
  registered,
  actionLabel = "View Details",
  bookingDisabled = false,
  title,
  attendeeIcon,
  attendeeInfo,
  badge,
  status = "Open",
}) => {
  // Determine badge variant based on status
  const statusBadgeVariant = status === "Open" ? "default" : "secondary"
  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>{title}</div>
          <Badge className="pointer-events-none" variant={statusBadgeVariant}>
            {status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-palette-jungleGreen500" />
          <div>
            <div className="font-medium">{format(date, "MMMM d, yyyy")}</div>
            <div className="text-sm text-palette-jungleGreen500">{timeInfo}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <MapPinIcon className="h-4 w-4 text-palette-jungleGreen500" />
          <div className="text-sm">{location}</div>
        </div>
        <div className="flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-palette-jungleGreen500" />
          <div className="text-sm">
            Attendees: {registered}/{capacity}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm text-palette-jungleGreen600">
            {attendeeIcon}
            {attendeeInfo}
          </div>
          {badge}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between pt-2">
        <Button
          variant={bookingDisabled ? "secondary" : "default"}
          size="sm"
          disabled={bookingDisabled}
        >
          {actionLabel}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default EventCard
